import { getReferenceString } from '@medplum/core';
import { Badge, Flex, Group, Stack, Table, Text } from '@mantine/core';
import { PrescriptionOrderTimelineGroup } from '@/components/Timeline/types';
import { MedicationRequest } from '@medplum/fhirtypes';
import { HighlightableItem } from '@/components/Timeline/TimelineItems/HighlightableItem';
import { TimelineCard } from '@/components/Timeline/TimelineItems/TimelineCard';
import { DateTimeTooltip } from './DateTimeTooltip';
import { TeammateAvatar } from '@/components/TeamAvatar';

interface PrescriptionOrderTimelineItemProps {
  group: PrescriptionOrderTimelineGroup;
  highlightRef?: string | null;
}

export function PrescriptionOrderTimelineItem(props: PrescriptionOrderTimelineItemProps) {
  const { group, highlightRef } = props;

  const firstPrescriptionOrder = group.items[0];
  const prescriptionRef = firstPrescriptionOrder ? getReferenceString(firstPrescriptionOrder.reference) : undefined;

  return (
    <HighlightableItem itemRef={prescriptionRef!} highlightRef={highlightRef}>
      <TimelineCard header={<PrescriptionOrderHeader group={group} />}>
        <Stack gap="md">
          {/* Each group can contain multiple medication requests authored together.
              We render them in a condensed table (one row per medication) instead of one card per order. */}
          <Table striped highlightOnHover withRowBorders={false} verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Medication</Table.Th>
                <Table.Th>Dosage Instructions</Table.Th>
                <Table.Th>Qty / Supply (Days)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {group.items.map((item) => (
                <Table.Tr key={getReferenceString(item.reference)}>
                  <Table.Td>{item.medicationDisplay ?? 'Unknown medication'}</Table.Td>
                  <Table.Td>{item.dosageInstruction ?? '-'}</Table.Td>
                  <Table.Td>{formatQuantityWithSupplyDuration(item.medicationRequest, item.quantity) ?? '-'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      </TimelineCard>
    </HighlightableItem>
  );
}

interface PrescriptionOrderHeaderProps {
  group: PrescriptionOrderTimelineGroup;
}

function PrescriptionOrderHeader({ group }: PrescriptionOrderHeaderProps) {
  const prescriptionItem = group.items[0];
  const medicationRequest = prescriptionItem.medicationRequest;

  return (
    <Group justify="space-between" align="center">
      <Group>
        <Flex gap="xs" align="center">
          <Stack gap={2}>
            <Flex gap="xs" align="center">
              <Text size="md" fw={700} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Prescription order
              </Text>
              <PrescriptionStatusBadge status={medicationRequest.status} />
            </Flex>
          </Stack>
        </Flex>
      </Group>
      <Group gap="md" align="center">
        {/* Only the inlined resource: a bare Reference would make the avatar fetch it per card. */}
        <TeammateAvatar
          practitioner={medicationRequest.requester?.resource}
          name={medicationRequest.requester?.display}
          size={30}
          label={medicationRequest.requester?.display ?? 'Unknown Requester'}
        />
        {medicationRequest?.authoredOn && <DateTimeTooltip date={group.timestamp} />}
      </Group>
    </Group>
  );
}

function formatExpectedSupplyDuration(medicationRequest: MedicationRequest): string | undefined {
  const dispenseRequest = medicationRequest.dispenseRequest;
  if (!dispenseRequest) {
    return undefined;
  }

  const parts: string[] = [];

  const expectedSupplyDuration = dispenseRequest.expectedSupplyDuration;
  if (expectedSupplyDuration?.value !== undefined) {
    const unit = expectedSupplyDuration.unit ?? 'days';
    parts.push(`${expectedSupplyDuration.value} ${unit}`);
  }

  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function formatQuantityWithSupplyDuration(
  medicationRequest: MedicationRequest,
  formattedQuantity?: string
): string | undefined {
  const quantityPart = formattedQuantity;
  const supplyPart = formatExpectedSupplyDuration(medicationRequest);

  if (!quantityPart && !supplyPart) {
    return undefined;
  }

  if (quantityPart && supplyPart) {
    return `${quantityPart} / ${supplyPart}`;
  }

  return quantityPart ?? supplyPart ?? undefined;
}

function PrescriptionStatusBadge({ status }: { status?: string }) {
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'completed':
        return 'blue';
      case 'cancelled':
      case 'stopped':
        return 'red';
      case 'on-hold':
        return 'yellow';
      case 'draft':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'stopped':
        return 'Stopped';
      case 'on-hold':
        return 'On Hold';
      case 'draft':
        return 'Draft';
      case 'entered-in-error':
        return 'Error';
      default:
        return status ?? 'Unknown';
    }
  };

  return (
    <Badge size="sm" color={getStatusColor(status)} variant="light">
      {getStatusLabel(status)}
    </Badge>
  );
}
